import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Sparkles, Download, Image as ImageIcon, Upload, Wand2, ZoomIn, Scissors, X, Heart, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const GREEN = "#00DC82";

const PRESETS = [
  { key: "realistic", label: "Realistic", grad: "linear-gradient(140deg,#3a4a5f,#96a7bd)" },
  { key: "cinematic", label: "Cinematic", grad: "linear-gradient(140deg,#0d2b3e,#e0813e)" },
  { key: "3D render", label: "3D Render", grad: "linear-gradient(140deg,#4527a0,#29b6f6)" },
  { key: "digital art", label: "Digital Art", grad: "linear-gradient(140deg,#aa2b74,#7b2ff7)" },
  { key: "anime", label: "Anime", grad: "linear-gradient(140deg,#ff6ec4,#42c6ff)" },
  { key: "product photography", label: "Product", grad: "linear-gradient(140deg,#0f3d2e,#7ac9a2)" },
  { key: "portrait", label: "Portrait", grad: "linear-gradient(140deg,#5d4037,#ffab91)" },
  { key: "minimal", label: "Minimal", grad: "linear-gradient(140deg,#37383f,#c9cad1)" },
];

const RATIOS = [
  { key: "1:1", label: "Square", w: 18, h: 18 },
  { key: "16:9", label: "Wide", w: 24, h: 14 },
  { key: "9:16", label: "Portrait", w: 12, h: 22 },
  { key: "4:3", label: "Classic", w: 22, h: 16 },
];
const COUNTS = [1, 2, 4];

export default function KVisual() {
  const { refreshUser } = useAuth();
  const [tab, setTab] = useState("generate");

  // Generate state
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState(PRESETS[0].key);
  const [ratio, setRatio] = useState(RATIOS[0].key);
  const [count, setCount] = useState(1);

  // Edit / ops state
  const [editFile, setEditFile] = useState(null);
  const [editFileUrl, setEditFileUrl] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [opFile, setOpFile] = useState(null);
  const [opFileUrl, setOpFileUrl] = useState(null);

  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]); // [{url, gen_id}]
  const [history, setHistory] = useState([]);
  const [historyTab, setHistoryTab] = useState("all");

  const fileInEditRef = useRef(null);
  const fileInOpRef = useRef(null);

  const loadHistory = () => api.get("/generations", { params: { kind: "image", limit: 30 } }).then((r) => setHistory(r.data.items)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const pickFile = (setter, urlSetter) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setter(f);
    urlSetter(URL.createObjectURL(f));
  };

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Enter a prompt");
    setBusy(true);
    try {
      const { data } = await api.post("/agents/image", { prompt, style, aspect_ratio: ratio, count });
      setResults(data.items || [{ url: data.url, gen_id: data.gen_id }]);
      toast.success(`${data.generated || 1} image${(data.generated || 1) > 1 ? "s" : ""} generated`);
      if (data.failed > 0) toast.warning(`${data.failed} image(s) failed — you were only charged for successful ones`);
      refreshUser(); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const runOp = async (endpoint, formExtra = {}, file = opFile) => {
    if (!file) return toast.error("Upload an image first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(formExtra).forEach(([k, v]) => fd.append(k, v));
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResults([{ url: data.url, gen_id: data.gen_id }]);
      toast.success("Done");
      refreshUser(); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operation failed");
    } finally { setBusy(false); }
  };

  const makeVariation = async (url, srcPrompt) => {
    setBusy(true);
    try {
      const blob = await fetch(`${BACKEND_URL}${url}`).then((r) => r.blob());
      const file = new File([blob], "source.png", { type: blob.type || "image/png" });
      await runOp("/agents/image/edit", {
        prompt: `Create a fresh variation of this image: keep the same subject and style${srcPrompt ? ` (${srcPrompt.slice(0, 120)})` : ""}, but change the composition, angle or details.`,
      }, file);
    } catch {
      toast.error("Could not load source image");
      setBusy(false);
    }
  };

  const toggleFavorite = async (gen_id) => {
    try {
      const { data } = await api.post(`/generations/${gen_id}/favorite`);
      setHistory((h) => h.map((g) => (g.gen_id === gen_id ? { ...g, favorite: data.favorite } : g)));
    } catch { toast.error("Could not update favorite"); }
  };

  const cost = count * 5;
  const shownHistory = historyTab === "favorites" ? history.filter((g) => g.favorite) : history;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto kr-page" data-module="image">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="kr-overline" style={{ color: GREEN }}>[ IMAGE GENERATOR ]</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Image Generator</h1>
          <p className="text-white/55 text-sm mt-1.5">Create, edit & enhance images with AI — generation, editing, upscaling and background removal.</p>
        </div>
        <span className="text-xs font-mono text-white/40">5 credits / image</span>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="ac-hscroll">
          <TabsList className="bg-[#0B0D12] border border-white/5 rounded-sm h-auto">
            <TabsTrigger value="generate" className="data-[state=active]:bg-white/10 px-4 py-2" data-testid="kvisual-tab-generate"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate</TabsTrigger>
            <TabsTrigger value="edit" className="data-[state=active]:bg-white/10 px-4 py-2" data-testid="kvisual-tab-edit"><Wand2 className="h-3.5 w-3.5 mr-1.5" /> Edit</TabsTrigger>
            <TabsTrigger value="upscale" className="data-[state=active]:bg-white/10 px-4 py-2" data-testid="kvisual-tab-upscale"><ZoomIn className="h-3.5 w-3.5 mr-1.5" /> Upscale</TabsTrigger>
            <TabsTrigger value="bgremove" className="data-[state=active]:bg-white/10 px-4 py-2" data-testid="kvisual-tab-bgremove"><Scissors className="h-3.5 w-3.5 mr-1.5" /> Remove BG</TabsTrigger>
          </TabsList>
        </div>

        <div className="grid grid-cols-12 gap-4 mt-6">
          {/* Left panel */}
          <div className="col-span-12 lg:col-span-4 kr-card p-4 sm:p-6 space-y-5 h-fit lg:sticky lg:top-[88px]">
            <TabsContent value="generate" className="m-0 space-y-5">
              <div>
                <div className="kr-overline mb-2">[ PROMPT ]</div>
                <Textarea data-testid="kvisual-prompt" rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe your image… e.g. A futuristic luxury watch on black marble" className="bg-[#07080B] border-white/10 resize-none text-base" />
              </div>

              {/* Style preset cards — swipeable on mobile */}
              <div>
                <div className="kr-overline mb-2">CHOOSE A STYLE</div>
                <div className="ac-hscroll gap-2.5 pb-1 lg:grid lg:grid-cols-4 lg:overflow-visible" data-testid="kvisual-style-presets">
                  {PRESETS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setStyle(p.key)}
                      className={`shrink-0 w-[76px] lg:w-auto rounded-xl overflow-hidden border transition-all ${style === p.key ? "ring-2" : "opacity-80 hover:opacity-100"}`}
                      style={{ borderColor: style === p.key ? GREEN : "rgba(255,255,255,0.08)", "--tw-ring-color": `${GREEN}90` }}
                      data-testid={`kvisual-preset-${p.key.replace(/\s+/g, "-")}`}
                    >
                      <div className="h-12" style={{ background: p.grad }} />
                      <div className={`text-[10px] py-1.5 text-center font-medium ${style === p.key ? "text-white bg-white/[0.06]" : "text-white/55 bg-white/[0.02]"}`}>{p.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Aspect ratio tiles */}
              <div>
                <div className="kr-overline mb-2">ASPECT RATIO</div>
                <div className="grid grid-cols-4 gap-2" data-testid="kvisual-ratio-tiles">
                  {RATIOS.map((r) => (
                    <button
                      key={r.key}
                      onClick={() => setRatio(r.key)}
                      className={`p-2.5 rounded-lg border flex flex-col items-center gap-1.5 min-h-[60px] transition-colors ${ratio === r.key ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}
                      style={{ borderColor: ratio === r.key ? GREEN : "rgba(255,255,255,0.08)" }}
                      data-testid={`kvisual-ratio-${r.key.replace(":", "x")}`}
                    >
                      <span className="rounded-[3px] border" style={{ width: r.w, height: r.h, borderColor: ratio === r.key ? GREEN : "rgba(255,255,255,0.35)" }} />
                      <span className={`text-[10px] font-mono ${ratio === r.key ? "text-white" : "text-white/45"}`}>{r.key}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Number of images */}
              <div>
                <div className="kr-overline mb-2">NUMBER OF IMAGES</div>
                <div className="flex gap-2" data-testid="kvisual-count-picker">
                  {COUNTS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCount(c)}
                      className={`flex-1 h-11 rounded-lg border font-mono text-sm transition-colors ${count === c ? "text-black font-bold" : "text-white/60 hover:text-white hover:bg-white/[0.03]"}`}
                      style={{ borderColor: count === c ? GREEN : "rgba(255,255,255,0.08)", background: count === c ? GREEN : "transparent" }}
                      data-testid={`kvisual-count-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sticky generate on mobile */}
              <div className="sticky bottom-[calc(env(safe-area-inset-bottom)+84px)] lg:static z-10">
                <Button onClick={generate} disabled={busy} className="w-full rounded-xl h-12 font-semibold text-white border-0 shadow-[0_0_24px_rgba(255,58,168,0.25)]" style={{ background: "linear-gradient(90deg,#FF3AA8,#8A52FF)" }} data-testid="kvisual-generate-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Generate · {cost} cr</>}
                </Button>
                <div className="text-center text-[11px] text-white/35 mt-1.5">Estimated cost: {cost} credits ({count} × 5)</div>
              </div>
            </TabsContent>

            <TabsContent value="edit" className="m-0 space-y-4">
              <div className="kr-overline">[ EDIT IMAGE ]</div>
              <FileDrop file={editFile} fileUrl={editFileUrl} onClick={() => fileInEditRef.current?.click()} onClear={() => { setEditFile(null); setEditFileUrl(null); }} testid="kvisual-edit-drop" />
              <input ref={fileInEditRef} type="file" accept="image/*" className="hidden" onChange={pickFile(setEditFile, setEditFileUrl)} data-testid="kvisual-edit-fileinput" />
              <Textarea data-testid="kvisual-edit-prompt" rows={4} value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} placeholder="Change the background to a starry night sky" className="bg-[#07080B] border-white/10 resize-none" />
              <Button onClick={() => { if (!editPrompt.trim()) return toast.error("Describe the edit"); runOp("/agents/image/edit", { prompt: editPrompt }, editFile); }} disabled={busy || !editFile} className="w-full bg-[#00DC82] hover:bg-[#00DC82]/90 rounded-md h-11 font-semibold text-black" data-testid="kvisual-edit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-2" /> Apply edit · 5 cr</>}
              </Button>
            </TabsContent>

            <TabsContent value="upscale" className="m-0 space-y-4">
              <div className="kr-overline">[ UPSCALE 2× ]</div>
              <FileDrop file={opFile} fileUrl={opFileUrl} onClick={() => fileInOpRef.current?.click()} onClear={() => { setOpFile(null); setOpFileUrl(null); }} testid="kvisual-upscale-drop" />
              <p className="text-xs text-white/40 leading-relaxed">Sharpens detail, removes noise and JPEG artifacts.</p>
              <Button onClick={() => runOp("/agents/image/upscale")} disabled={busy || !opFile} className="w-full bg-[#00DC82] hover:bg-[#00DC82]/90 rounded-md h-11 font-semibold text-black" data-testid="kvisual-upscale-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ZoomIn className="h-4 w-4 mr-2" /> Upscale · 5 cr</>}
              </Button>
            </TabsContent>

            <TabsContent value="bgremove" className="m-0 space-y-4">
              <div className="kr-overline">[ REMOVE BACKGROUND ]</div>
              <FileDrop file={opFile} fileUrl={opFileUrl} onClick={() => fileInOpRef.current?.click()} onClear={() => { setOpFile(null); setOpFileUrl(null); }} testid="kvisual-bgremove-drop" />
              <p className="text-xs text-white/40 leading-relaxed">Clean cutout with hair-level detail.</p>
              <Button onClick={() => runOp("/agents/image/bg-remove")} disabled={busy || !opFile} className="w-full bg-[#00DC82] hover:bg-[#00DC82]/90 rounded-md h-11 font-semibold text-black" data-testid="kvisual-bgremove-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Scissors className="h-4 w-4 mr-2" /> Remove background · 5 cr</>}
              </Button>
            </TabsContent>

            <input ref={fileInOpRef} type="file" accept="image/*" className="hidden" onChange={pickFile(setOpFile, setOpFileUrl)} data-testid="kvisual-op-fileinput" />
          </div>

          {/* Output panel */}
          <div className="col-span-12 lg:col-span-8 kr-card p-4 sm:p-6 min-h-[400px] flex flex-col">
            <div className="kr-overline">[ OUTPUT ]</div>
            {busy && (
              <div className="flex-1 flex flex-col items-center justify-center text-white/40 py-16">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 border-2 border-[#00DC82]/30 rounded-full" />
                  <div className="absolute inset-0 border-2 border-transparent border-t-[#00DC82] rounded-full animate-spin" />
                </div>
                <div className="text-xs mt-4 font-mono kr-pulse-soft">RENDERING…</div>
              </div>
            )}
            {!busy && results.length === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center text-white/30 py-16">
                <ImageIcon className="h-10 w-10 mb-3" />
                <div className="text-sm">Your results will appear here.</div>
              </div>
            )}
            {!busy && results.length > 0 && (
              <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className={`mt-4 grid gap-3 ${results.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`} data-testid="kvisual-results">
                {results.map((r, i) => (
                  <div key={r.gen_id || i} className="group relative border border-white/10 rounded-lg overflow-hidden bg-[repeating-conic-gradient(#14171f_0%_25%,#0B0D12_0%_50%)] bg-[length:20px_20px]">
                    <img src={`${BACKEND_URL}${r.url}`} alt="Result" className="w-full max-h-[520px] object-contain mx-auto" data-testid={i === 0 ? "kvisual-result" : undefined} />
                    <div className="flex gap-2 p-2.5 bg-black/40 border-t border-white/[0.06]">
                      <a href={`${BACKEND_URL}${r.url}`} download target="_blank" rel="noreferrer" className="flex-1">
                        <Button variant="outline" className="w-full h-9 border-white/10 hover:bg-white/5 hover:text-white text-xs" data-testid={i === 0 ? "kvisual-download" : undefined}><Download className="h-3.5 w-3.5 mr-1.5" /> Download</Button>
                      </a>
                      <Button variant="outline" onClick={() => makeVariation(r.url, prompt)} className="flex-1 h-9 border-white/10 hover:bg-white/5 hover:text-white text-xs" data-testid={i === 0 ? "kvisual-variations-btn" : undefined}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Variations · 5 cr
                      </Button>
                      {r.gen_id && (
                        <button onClick={() => toggleFavorite(r.gen_id)} className="w-9 h-9 rounded-md border border-white/10 flex items-center justify-center text-white/50 hover:text-[#FF3AA8]" aria-label="Favorite" data-testid={i === 0 ? "kvisual-result-favorite" : undefined}>
                          <Heart className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </Tabs>

      {/* History */}
      <div className="mt-10">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="kr-overline">[ RECENT GENERATIONS ]</div>
          <div className="flex gap-1.5" data-testid="history-filter">
            <button onClick={() => setHistoryTab("all")} className={`px-3.5 py-1.5 rounded-full text-xs border min-h-[32px] ${historyTab === "all" ? "text-black font-semibold" : "text-white/55 border-white/10 hover:text-white"}`} style={historyTab === "all" ? { background: GREEN, borderColor: GREEN } : {}} data-testid="history-tab-all">All</button>
            <button onClick={() => setHistoryTab("favorites")} className={`px-3.5 py-1.5 rounded-full text-xs border min-h-[32px] flex items-center gap-1.5 ${historyTab === "favorites" ? "text-black font-semibold" : "text-white/55 border-white/10 hover:text-white"}`} style={historyTab === "favorites" ? { background: GREEN, borderColor: GREEN } : {}} data-testid="history-tab-favorites"><Heart className="h-3 w-3" /> Favorites</button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
          {shownHistory.map((g) => (
            <div key={g.gen_id} className="group relative aspect-square overflow-hidden rounded-lg border border-white/5 hover:border-white/25 transition-all" data-testid={`history-img-${g.gen_id}`}>
              <button onClick={() => setResults([{ url: g.result, gen_id: g.gen_id }])} className="absolute inset-0">
                <img src={`${BACKEND_URL}${g.result}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
              </button>
              <button
                onClick={() => toggleFavorite(g.gen_id)}
                className={`absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center border border-white/15 backdrop-blur-sm transition-opacity ${g.favorite ? "bg-black/50" : "bg-black/40 opacity-0 group-hover:opacity-100 max-lg:opacity-100"}`}
                aria-label="Toggle favorite"
                data-testid={`history-fav-${g.gen_id}`}
              >
                <Heart className="h-3.5 w-3.5" style={{ color: g.favorite ? "#FF3AA8" : "rgba(255,255,255,0.6)", fill: g.favorite ? "#FF3AA8" : "none" }} />
              </button>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2 pt-6 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between gap-1">
                <div className="text-[10px] text-white/80 line-clamp-2 flex-1">{g.prompt}</div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => makeVariation(g.result, g.prompt)} className="w-7 h-7 rounded bg-white/10 flex items-center justify-center text-white/70 hover:text-white" title="Variations" aria-label="Variations" data-testid={`history-var-${g.gen_id}`}><RefreshCw className="h-3 w-3" /></button>
                  <button onClick={() => { setPrompt(g.prompt); setTab("generate"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="w-7 h-7 rounded bg-white/10 flex items-center justify-center text-white/70 hover:text-white" title="Reuse prompt" aria-label="Reuse prompt" data-testid={`history-reuse-${g.gen_id}`}><RotateCcw className="h-3 w-3" /></button>
                </div>
              </div>
            </div>
          ))}
          {shownHistory.length === 0 && (
            <div className="col-span-full text-sm text-white/30 py-8 text-center">
              {historyTab === "favorites" ? "No favorites yet — tap the heart on any generation." : "No history yet."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileDrop({ file, fileUrl, onClick, onClear, testid }) {
  if (file && fileUrl) {
    return (
      <div className="relative border border-white/10 rounded-lg overflow-hidden">
        <img src={fileUrl} alt="" className="w-full max-h-48 object-contain bg-[#07080B]" />
        <button onClick={onClear} className="absolute top-1 right-1 p-1.5 bg-black/60 border border-white/10 hover:bg-red-500/20 text-white/80 hover:text-red-300 rounded" data-testid={`${testid}-clear`}>
          <X className="h-3 w-3" />
        </button>
        <div className="px-2 py-1 text-xs text-white/50 bg-black/40 truncate">{file.name}</div>
      </div>
    );
  }
  return (
    <button onClick={onClick} className="w-full border border-dashed border-white/15 hover:border-[#00DC82]/50 hover:bg-white/[0.02] rounded-lg transition py-8 flex flex-col items-center justify-center" data-testid={testid}>
      <Upload className="h-6 w-6 text-white/40" />
      <div className="text-xs text-white/60 mt-2">Click to upload an image</div>
      <div className="kr-overline mt-1">PNG · JPG · WEBP</div>
    </button>
  );
}
